import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useLoading } from '../utils/loading/LoadingContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../pages/styles/palletlist.css';
import '../pages/styles/modern-pages.css';

const Pallets = () => {
    const [pallets, setPallets] = useState([]);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchActive, setSearchActive] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [activeTab, setActiveTab] = useState('available');
    const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;
    const navigate = useNavigate();
    const { isLoading, setLoading } = useLoading();
    const [jumpPage, setJumpPage] = useState('');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [statusConfirmation, setStatusConfirmation] = useState(null);

    useEffect(() => {
        fetchPallets(activeTab, 1);
    }, [activeTab]);

    useEffect(() => {
        if (deleteTarget === null && statusConfirmation === null) return undefined;

        const handleDialogKeyDown = (event) => {
            if (event.key === 'Escape') {
                setDeleteTarget(null);
                setDeleteConfirmation('');
                setStatusConfirmation(null);
            }
        };

        document.addEventListener('keydown', handleDialogKeyDown);
        return () => document.removeEventListener('keydown', handleDialogKeyDown);
    }, [deleteTarget, statusConfirmation]);

    const fetchPallets = (status, page = 1) => {
        setLoading(true);
        fetch(`${baseUrl}/api/pallets/${status}/?page=${page}`)
            .then(response => response.json())
            .then(data => {
                setPallets(data.results);
                setNextPage(data.next);
                setPrevPage(data.previous);
                setTotalCount(data.count);
                setTotalPages(Math.ceil(data.count / 10)); // assuming default page size is 10
                setCurrentPage(page);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching pallets:', error);
                setLoading(false);
            });
    };

    const changePalletStatus = (palletNumber, newStatus) => {
        fetch(`${baseUrl}/api/pallets/${palletNumber}/update_status/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: newStatus }),
        })
            .then(response => response.json())
            .then(data => {
                if (data.status === 'success') {
                    setStatusConfirmation(null);
                    if (searchActive) {
                        setPallets(currentPallets =>
                            currentPallets.map(pallet =>
                                pallet.pallet_number === palletNumber
                                    ? { ...pallet, status: newStatus }
                                    : pallet
                            )
                        );
                    } else {
                        fetchPallets(activeTab);
                    }
                    toast.success(`Pallet status updated to ${newStatus}`);
                } else {
                    toast.error(`Failed to update pallet status: ${data.message}`);
                }
            })
            .catch(error => {
                console.error('Error updating pallet status:', error);
                toast.error('Failed to update pallet status.');
            });
    };

    const openStatusConfirmation = (palletNumber, newStatus) => {
        setStatusConfirmation({ palletNumber, newStatus });
    };

    const openDeleteDialog = (palletNumber) => {
        setDeleteTarget(palletNumber);
        setDeleteConfirmation('');
    };

    const closeDeleteDialog = () => {
        setDeleteTarget(null);
        setDeleteConfirmation('');
    };

    const deletePallet = () => {
        if (
            deleteTarget === null ||
            deleteConfirmation.trim() !== String(deleteTarget)
        ) {
            return;
        }

        const palletNumber = deleteTarget;
        setLoading(true);
        fetch(`${baseUrl}/api/pallets/${palletNumber}/`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to delete pallet');
                }
                setPallets(currentPallets =>
                    currentPallets.filter(p => p.pallet_number !== palletNumber)
                );
                closeDeleteDialog();
                toast.success(`Pallet ${palletNumber} deleted.`);
            })
            .catch(error => {
                console.error('Error deleting pallet:', error);
                toast.error('Failed to delete pallet.');
            })
            .finally(() => setLoading(false));
    };

    const downloadPalletItems = (palletNumber) => {
        setLoading(true);
        fetch(`${baseUrl}/api/pallets/${palletNumber}/download/`)
            .then(response => response.blob())
            .then(blob => {
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Pallet_${palletNumber}_Items.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error downloading pallet items:', error);
                setLoading(false);
            });
    };

    const viewPallet = async (palletNumber) => {
        setLoading(true);
        try {
            const res = await fetch(`${baseUrl}/api/pallets/${palletNumber}/`);
            const data = await res.json();
            // Optionally store in state or pass to the detail page
            setLoading(false);
            navigate(`/pallets/${palletNumber}`, { state: { pallet: data } });
        } catch (err) {
            setLoading(false);
            console.error('Error viewing pallet:', err);
            toast.error('Failed to fetch pallet details.');
        }
    };

    // const viewPallet = (palletNumber) => {
    //     navigate(`/pallets/${palletNumber}`);
    // };

    const generatePublicLink = (pallet) => {
        const publicLink = `${baseUrl}/public/download/pallet/${pallet.public_token}/`;
        navigator.clipboard.writeText(publicLink).then(() => {
            toast.success(`Public link copied to clipboard: ${publicLink}`);
        });
    };

    const printQRCode = (pallet) => {
        const publicLink = `${baseUrl}/public/download/pallet/${pallet.public_token}/`;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Print QR Code</title>
                <style>
                   body { 
                    display: flex; 
                    flex-direction: column;
                    justify-content: center; 
                    align-items: center; 
                    text-align: center; 
                    font-family: Arial, sans-serif; 
                    margin: 0; 
                    height: 100vh; 
                }
                h2 { 
                    margin-bottom: 20px; 
                }
                .qr-code { 
                    margin-top: 20px; 
                }
                </style>
            </head>
            <body>
                <h2>Pallet: ${pallet.pallet_number}</h2>
                <div class="qr-code">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(publicLink)}" alt="QR Code" />
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const handleTabChange = (tab) => {
        setSearchTerm('');
        setSearchActive(false);
        setActiveTab(tab);
    };

    const fetchAllPalletPages = async (status) => {
        const statusPallets = [];
        const visitedPages = new Set();
        let pageUrl = `${baseUrl}/api/pallets/${status}/?page=1`;

        while (pageUrl && !visitedPages.has(pageUrl)) {
            visitedPages.add(pageUrl);
            const response = await fetch(pageUrl);
            if (!response.ok) {
                throw new Error(`Failed to load ${status} pallets`);
            }

            const data = await response.json();
            const pageResults = Array.isArray(data.results) ? data.results : [];
            statusPallets.push(
                ...pageResults.map((pallet) => ({
                    ...pallet,
                    status: pallet.status || status,
                }))
            );
            pageUrl = data.next;
        }

        return statusPallets;
    };

    const handleSearch = async () => {
        const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
        if (!normalizedSearch) {
            toast.error('Please enter a pallet number');
            return;
        }

        setLoading(true);
        try {
            const [availablePallets, soldPallets] = await Promise.all([
                fetchAllPalletPages('available'),
                fetchAllPalletPages('sold'),
            ]);

            const matches = [...availablePallets, ...soldPallets].filter((pallet) =>
                String(pallet.pallet_number ?? '')
                    .toLocaleLowerCase()
                    .includes(normalizedSearch)
            );
            const uniqueMatches = Array.from(
                new Map(
                    matches.map((pallet) => [String(pallet.pallet_number), pallet])
                ).values()
            );

            setPallets(uniqueMatches);
            setSearchActive(true);
            setNextPage(null);
            setPrevPage(null);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalCount(uniqueMatches.length);

            if (uniqueMatches.length === 0) {
                toast.info('No matching pallets found.');
            }
        } catch (error) {
            console.error('Search error:', error);
            toast.error('Could not search pallets. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSearchActive(false);
        fetchPallets(activeTab, 1); // Reset to normal list
    };


    return (
        <div className='modern-page palletList-body'>
          <div className="modern-page-shell">
            <header className="modern-page-header pallet-list-header">
              <h1 className="modern-page-title">Pallets</h1>
              <div className="pallet-header-controls">
                <div className="tab-container" role="tablist" aria-label="Pallet status">
                    <button role="tab" aria-selected={activeTab === 'available'} className={`tab ${activeTab === 'available' ? 'active' : ''}`} onClick={() => handleTabChange('available')}>Available</button>
                    <button role="tab" aria-selected={activeTab === 'sold'} className={`tab ${activeTab === 'sold' ? 'active' : ''}`} onClick={() => handleTabChange('sold')}>Sold</button>
                </div>
                <div className="search-bar">
                    <Search size={17} aria-hidden="true" />
                    <input
                        type="text"
                        placeholder="Search pallet number"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSearch();
                        }}
                    />
                    {searchActive && (
                        <button onClick={clearSearch} className='search-clear-button' aria-label="Clear search">
                            <X size={16} />
                        </button>
                    )}
                    <button
                        onClick={handleSearch}
                        className='search-button'
                        disabled={!searchTerm.trim() || isLoading}
                    >
                        {isLoading ? (
                            <span className="modern-dot-loader" aria-label="Searching">
                                <span></span><span></span><span></span>
                            </span>
                        ) : 'Search'}
                    </button>
                </div>
              </div>
            </header>

            <section className="modern-panel pallet-table-panel">
              <div className="modern-panel-header">
                <h2>{searchActive ? 'Search Results' : activeTab === 'available' ? 'Available Pallets' : 'Sold Pallets'}</h2>
                <span className="modern-page-meta">{searchActive ? pallets.length : totalCount} pallets</span>
              </div>
              <div className="pallet-table-scroll">
              <table className='palletList-table'>
                <thead>
                    <tr>
                        <th>Pallet Number</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {pallets.map(pallet => {
                        const palletStatus = String(pallet.status || activeTab).toLowerCase();
                        return (
                        <tr key={pallet.pallet_number}>
                            <td><strong>{pallet.pallet_number}</strong></td>
                            <td>{new Date(pallet.created_at).toLocaleString()}</td>
                            <td>
                                <div className='actions-div'>
                                    <button className='action-view' onClick={() => viewPallet(pallet.pallet_number)}>View</button>
                                    
                                    <button className='action-download' onClick={() => downloadPalletItems(pallet.pallet_number)}>Download</button>
                                    <button className='action-link' onClick={() => generatePublicLink(pallet)}>Copy Public Link</button>
                                    <button className='action-print' onClick={() => printQRCode(pallet)}>Print QR</button>
                                    {palletStatus === 'available' && (
                                        <button className='action-mark-sold' onClick={() => openStatusConfirmation(pallet.pallet_number, 'sold')}>Mark as Sold</button>
                                    )}
                                    {palletStatus === 'sold' && (
                                        <button className='action-mark-available' onClick={() => openStatusConfirmation(pallet.pallet_number, 'available')}>Mark as Available</button>
                                    )}
                                    <button className='action-delete' onClick={() => openDeleteDialog(pallet.pallet_number)}>Delete</button>
                                </div>
                            </td>
                        </tr>
                    )})}
                    {!isLoading && pallets.length === 0 && (
                      <tr><td colSpan="3" className="pallet-empty">No pallets found.</td></tr>
                    )}
                </tbody>
              </table>
              </div>
            </section>
            {!searchActive && <div className="pagination-controls">
                <button
                    disabled={!prevPage}
                    onClick={() => fetchPallets(activeTab, currentPage - 1)}
                >
                    Previous
                </button>
                <span> Page {currentPage} of {totalPages} </span>
                <button
                    disabled={!nextPage}
                    onClick={() => fetchPallets(activeTab, currentPage + 1)}
                >
                    Next
                </button>
                <input
                    type="number"
                    min="1"
                    max={totalPages}
                    placeholder="Jump to page"
                    value={jumpPage}
                    onChange={(e) => setJumpPage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            const pageNum = parseInt(jumpPage);
                            if (pageNum >= 1 && pageNum <= totalPages) {
                                fetchPallets(activeTab, pageNum);
                            }
                        }
                    }}
                />

                <button
                    onClick={() => fetchPallets(activeTab, parseInt(jumpPage))}
                    disabled={jumpPage < 1 || jumpPage > totalPages || isNaN(jumpPage)}
                >
                    Jump
                </button>
            </div>}
            {deleteTarget !== null && (
                <div className="pallet-delete-overlay" role="presentation" onMouseDown={closeDeleteDialog}>
                    <section
                        className="pallet-delete-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pallet-delete-title"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className="pallet-delete-icon" aria-hidden="true">!</div>
                        <h2 id="pallet-delete-title">Delete pallet?</h2>
                        <p>
                            This permanently deletes pallet <strong>{deleteTarget}</strong> and cannot be undone.
                        </p>
                        <label className="pallet-delete-field">
                            <span>Type <strong>{deleteTarget}</strong> to confirm</span>
                            <input
                                type="text"
                                value={deleteConfirmation}
                                onChange={(event) => setDeleteConfirmation(event.target.value)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' &&
                                        deleteConfirmation.trim() === String(deleteTarget)
                                    ) {
                                        deletePallet();
                                    }
                                }}
                                autoFocus
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </label>
                        <div className="pallet-delete-actions">
                            <button type="button" className="delete-cancel-button" onClick={closeDeleteDialog}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="delete-confirm-button"
                                onClick={deletePallet}
                                disabled={deleteConfirmation.trim() !== String(deleteTarget)}
                            >
                                Delete pallet
                            </button>
                        </div>
                    </section>
                </div>
            )}
            {statusConfirmation !== null && (
                <div
                    className="pallet-status-overlay"
                    role="presentation"
                    onMouseDown={() => setStatusConfirmation(null)}
                >
                    <section
                        className="pallet-status-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="pallet-status-title"
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className="pallet-status-icon" aria-hidden="true">?</div>
                        <h2 id="pallet-status-title">
                            Mark pallet as {statusConfirmation.newStatus === 'sold' ? 'Sold' : 'Available'}?
                        </h2>
                        <p>
                            Pallet <strong>{statusConfirmation.palletNumber}</strong> will move to the{' '}
                            <strong>{statusConfirmation.newStatus === 'sold' ? 'Sold' : 'Available'}</strong> list.
                        </p>
                        <div className="pallet-status-actions">
                            <button
                                type="button"
                                className="status-cancel-button"
                                onClick={() => setStatusConfirmation(null)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="status-confirm-button"
                                onClick={() => changePalletStatus(
                                    statusConfirmation.palletNumber,
                                    statusConfirmation.newStatus
                                )}
                            >
                                Confirm
                            </button>
                        </div>
                    </section>
                </div>
            )}
            <ToastContainer />
          </div>
        </div>
    );
};

export default Pallets;
